/**
 * Copy the audit helpers hand to components. A plain string is data (a name, an email,
 * a raw code) and shows as it is; a message is an i18n key with its values, and a value
 * may itself be a message, so a summary can nest a translated "empty" inside its line.
 */
export interface AuditMessage {
    key: string;
    values?: Readonly<Record<string, AuditText | number>>;
    /** Shown when the key has no translation, such as an entity type added later. */
    fallback?: string;
}

export type AuditText = string | AuditMessage;

/** The part of i18next's `t` the audit texts need; the `t` of useTranslation fits it. */
export type AuditTranslate = (key: string, options?: Record<string, unknown>) => string;

export function auditMessage(
    key: string,
    values?: Readonly<Record<string, AuditText | number>>,
    fallback?: string
): AuditMessage {
    const message: AuditMessage = { key };
    if (values !== undefined) message.values = values;
    if (fallback !== undefined) message.fallback = fallback;
    return message;
}

/** Joins texts with the translated list separator, nesting from the right. */
export function joinAuditTexts(texts: readonly AuditText[]): AuditText {
    if (texts.length === 0) return '';
    const [head, ...tail] = texts;
    if (tail.length === 0) return head;
    return auditMessage('auditLog.text.listJoin', { head, tail: joinAuditTexts(tail) });
}

/** Writes a number the way the page's language does, such as 1,834 or 1 834. */
export type AuditNumberFormat = (value: number) => string;

/**
 * Without `formatNumber` numbers go to `t` as they are. With it, `count` still goes as a
 * number, since i18next picks the plural form only from a numeric count, and the formatted
 * text reaches the string through `replace`, which i18next interpolates in its place.
 */
export function renderAuditText(
    text: AuditText,
    t: AuditTranslate,
    formatNumber?: AuditNumberFormat
): string {
    if (typeof text === 'string') return text;
    const values: Record<string, string | number> = {};
    for (const [name, value] of Object.entries(text.values ?? {})) {
        if (typeof value !== 'number') values[name] = renderAuditText(value, t, formatNumber);
        else values[name] = formatNumber === undefined ? value : formatNumber(value);
    }
    const count = text.values?.count;
    const options: Record<string, unknown> =
        formatNumber !== undefined && typeof count === 'number'
            ? { count, replace: values }
            : values;
    return text.fallback === undefined
        ? t(text.key, options)
        : t(text.key, { ...options, defaultValue: text.fallback });
}
